import React, { FC, useCallback, useEffect, useMemo } from "react";
import { DataGrid, GridColumns, GridOverlay, GridRowData } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useStyles } from "../styles";
import {
  ExternalOrderRecordEntity,
  useGetNotReturnedEquipmentQuery
} from "../../../../../graphql/graphQlApiHooks";
import { useNavigate } from "react-router-dom";
import {
    IdCell,
    EquipmentCell,
    OwnerCell,
    ContactsCell,
    AddressCell,
    StatusCell
} from "./CellRenderers";

interface Props {

}

const StyledGridOverlay = styled(GridOverlay)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  '& .empty-icon': {
    width: 120,
    height: 120,
    marginBottom: theme.spacing(3),
    opacity: 0.6,
    color: theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[600],
  },
  '& .empty-title': {
    fontSize: '18px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  '& .empty-description': {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    maxWidth: 400,
  },
}));

const NoRowsOverlay = () => (
  <StyledGridOverlay>
    <svg
      className="empty-icon"
      viewBox="0 0 200 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.7">
        {/* Magnifying glass */}
        <circle cx="85" cy="75" r="35" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path
          d="M110 100L130 120"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Document lines */}
        <rect x="30" y="130" width="140" height="6" rx="3" fill="currentColor" opacity="0.4" />
        <rect x="30" y="142" width="120" height="6" rx="3" fill="currentColor" opacity="0.3" />
        <rect x="30" y="154" width="100" height="6" rx="3" fill="currentColor" opacity="0.2" />
      </g>
    </svg>
    <Typography className="empty-title">Данные не найдены</Typography>
    <Typography className="empty-description">
      По выбранным критериям не найдено ни одной записи
    </Typography>
  </StyledGridOverlay>
);

const EquipmentList: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [rows, setRows] = React.useState<readonly GridRowData[]>([]);

  const { data, loading } = useGetNotReturnedEquipmentQuery({
    pollInterval: 120000
  });

  useEffect(() => {
    if (data?.getNotReturnedEquipment) {
      setRows(data.getNotReturnedEquipment);
    }
  }, [data]);

  const handleDoubleClick = useCallback((row: ExternalOrderRecordEntity) => {
    navigate(`order/${row.externalOrder.id}`);
  }, [navigate]);

  const columns: GridColumns<ExternalOrderRecordEntity> = useMemo(() => [
    {
      field: "id", headerName: "№",
      flex: .5,
      renderCell: (params) => <IdCell row={params.row} />,
      minWidth: 110,
      disableColumnMenu: true
    },
    {
      field: "equipment",
      headerName: "Оборудование",
      renderCell: (params) => <EquipmentCell row={params.row} />,
      flex: 2,
      minWidth: 220,
      disableColumnMenu: true,
    },
    {
      field: "externalOrderOwner", headerName: "Заявитель",
      flex: 1,
      renderCell: (params) => <OwnerCell row={params.row} />,
      minWidth: 120,
      disableColumnMenu: true
    },
    {
      field: "receivingName",
      headerName: "Контакты для связи",
      renderCell: (params) => <ContactsCell row={params.row} />,
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
    },
    {
      field: "targetPointAddress",
      headerName: "Адрес",
      renderCell: (params) => <AddressCell row={params.row} />,
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
    },
    {
      field: "status",
      headerName: "Статус",
      renderCell: (params) => <StatusCell row={params.row} />,
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
    },
  ], []);

  return (
    <Box
      sx={{ height: "100%", width: "100%" }}
      className={classes.root}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        getRowHeight={() => 50}
        pageSize={50}
        disableSelectionOnClick={true}
        disableExtendRowFullWidth
        scrollbarSize={8}
        columnBuffer={3}
        columnThreshold={3}
        rowBuffer={5}
        rowThreshold={5}
        rowsPerPageOptions={[5, 10, 50, 100]}
        onCellDoubleClick={({ row }) => handleDoubleClick(row)}
        initialState={{
          sorting: {
            sortModel: [{ field: "status", sort: "asc" }]
          }
        }}
        components={{
          NoRowsOverlay: NoRowsOverlay,
        }}
      />

    </Box>
  );
};

export default EquipmentList;